import { ReactNode, useSyncExternalStore } from 'react';
import { createGlobalStyle } from 'styled-components';
import { getExperimentVariant } from 'hooks/use-experiment-routing';
import {
  getOptimizelyEdgeExperiments,
  OPTIMIZELY_EDGE_COOKIE_NAME,
} from 'lib/utils/get-optimizely-edge-experiments';
import { trackEvent } from 'lib/analytics';
import { trackFeatureFlag } from 'lib/datadog-rum';
import type { FooterDisclaimerConfig } from './FooterNavigation/footer-disclaimer';
import FooterLocale from 'locale/en/footer.json';

// The Cloudflare worker sets <body data-homepage-test="<experiment>:<variant>"> on full loads of `/`;
// both variants are server-rendered in DOM order and this CSS shows one.
export const HOMEPAGE_EXPERIMENT = 'cf_homepage_benefits_nov2026';
const HOMEPAGE_EXPERIMENT_ATTRIBUTE = 'data-homepage-test';
const VARIANT_ATTRIBUTE = 'data-homepage-variant';

const HOMEPAGE_VARIANTS = ['control', 'variant_1'] as const;

// Buckets visitors who reach `/` by client-side navigation, which the worker never sees.
// Turn on only while the worker runs the test, or they'd get variant_1 outside it.
const CLIENT_BUCKETING_ENABLED = true;
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

// Footnotes for variant_1's Benefits (°, **, ††) and ViaCap (Δ) modules
export const VARIANT_1_FOOTER_DISCLAIMER: FooterDisclaimerConfig = {
  lines: [
    `**${FooterLocale.mild_bothersome_bloating}`,
    'Δ Lactobacillus genera',
    `††${FooterLocale.ingredient_clinical_research}`,
  ],
  primaryStatementOverride:
    '°These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
};

export type HomepageVariant = (typeof HOMEPAGE_VARIANTS)[number];

const isHomepageVariant = (value: unknown): value is HomepageVariant =>
  HOMEPAGE_VARIANTS.includes(value as HomepageVariant);

const VARIANT_1 = `body[${HOMEPAGE_EXPERIMENT_ATTRIBUTE}='${HOMEPAGE_EXPERIMENT}:variant_1']`;

export const HomepageExperimentStyles = createGlobalStyle`
  [${VARIANT_ATTRIBUTE}='variant_1'] {
    display: none;
  }

  ${VARIANT_1} [${VARIANT_ATTRIBUTE}='control'] {
    display: none;
  }

  ${VARIANT_1} [${VARIANT_ATTRIBUTE}='variant_1'] {
    display: block;
  }
`;

let clientAssignment: HomepageVariant | undefined;

// Mirrors the worker: 50/50 split, stored in the edge cookie (which the worker honours on
// later loads of `/`), and reported the way analytics.tsx reports edge assignments.
// Runs once per page load; afterwards the cookie answers.
export const assignHomepageVariantOnClient = (): HomepageVariant => {
  if (clientAssignment) return clientAssignment;
  const variant: HomepageVariant =
    Math.random() < 0.5 ? 'control' : 'variant_1';
  clientAssignment = variant;

  const experiments = {
    ...(getOptimizelyEdgeExperiments() ?? {}),
    [HOMEPAGE_EXPERIMENT]: variant,
  };
  document.cookie = `${OPTIMIZELY_EDGE_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(experiments))}; path=/; max-age=${ONE_YEAR_IN_SECONDS}; samesite=lax; secure`;

  trackEvent('Experiment Viewed', {
    experimentName: HOMEPAGE_EXPERIMENT,
    variationName: variant,
  });
  trackFeatureFlag(HOMEPAGE_EXPERIMENT, variant);
  return variant;
};

const getVariant = (): HomepageVariant => {
  const forced = new URLSearchParams(window.location.search).get(
    HOMEPAGE_EXPERIMENT,
  );
  if (isHomepageVariant(forced)) return forced;

  const stamped = document.body.getAttribute(HOMEPAGE_EXPERIMENT_ATTRIBUTE);
  if (stamped?.startsWith(`${HOMEPAGE_EXPERIMENT}:`)) {
    const variant = stamped.slice(HOMEPAGE_EXPERIMENT.length + 1);
    return isHomepageVariant(variant) ? variant : 'control';
  }

  // Unstamped at `/` means the worker isn't running the test; the cookie only covers client-side navigations
  const [navigation] = performance.getEntriesByType('navigation');
  const loadedAtHome = navigation
    ? new URL(navigation.name).pathname === '/'
    : false;
  if (loadedAtHome) return 'control';

  const fromCookie = getExperimentVariant(HOMEPAGE_EXPERIMENT);
  if (isHomepageVariant(fromCookie)) return fromCookie;

  // The layout reads the variant on every route; only the homepage itself buckets
  if (CLIENT_BUCKETING_ENABLED && window.location.pathname === '/') {
    return assignHomepageVariantOnClient();
  }
  return 'control';
};

const subscribe = () => () => {};

// `null` while hydrating, so both variants render to match the server HTML
export const useHomepageVariant = (): HomepageVariant | null =>
  useSyncExternalStore(subscribe, getVariant, () => null);

interface HomepageVariantSlotProps {
  variant: HomepageVariant;
  activeVariant: HomepageVariant | null;
  children: ReactNode;
}

export const HomepageVariantSlot = ({
  variant,
  activeVariant,
  children,
}: HomepageVariantSlotProps) => {
  if (activeVariant !== null && activeVariant !== variant) return null;
  return (
    <div data-homepage-variant={activeVariant === null ? variant : undefined}>
      {children}
    </div>
  );
};
